import { useTranslation } from '@doccareplus/i18n';
import { numberUtils } from '@doccareplus/shared/lib';
import { Card } from '@doccareplus/ui';
import { useMyInvoicesQuery } from '../application/billing.query';
import { invoiceStatusKey, invoiceStatusTone } from '../domain/invoiceStatus';
import type { PortalInvoiceDto } from '../infrastructure/billing.api';
import { PortalLoadError } from '@/shared/ui/PortalLoadError';
import { BillingPageSkeleton } from './BillingSkeletons';

// V1 has no online payment gateway (see .ai/BusinessDomain/billing-payment.md, "Patient Portal Visibility") —
// this page is intentionally read-only: a balance + "Pay at clinic" note instead of any payment form/button.

function InvoiceRow({ invoice }: { invoice: PortalInvoiceDto }) {
  const { t } = useTranslation('portal');
  return (
    <Card className="flex items-center justify-between gap-3 p-3.5">
      <div className="min-w-0">
        <div className="font-mono text-[13.5px] font-semibold">{invoice.invoiceNumber}</div>
        <div className="text-[12px] text-[var(--text-3)]">{new Date(invoice.invoiceDate).toLocaleDateString()}</div>
      </div>
      <div className="flex shrink-0 items-center gap-2.5">
        <span className="text-[13.5px] font-semibold tabular-nums">
          {numberUtils.formatCurrency(invoice.total, invoice.currency)}
        </span>
        <span
          className={`inline-flex rounded-[20px] px-[9px] py-[2px] text-[11.5px] font-semibold ${invoiceStatusTone(invoice.status)}`}
        >
          {t(`billing.status.${invoiceStatusKey(invoice.status)}`, invoice.status)}
        </span>
      </div>
    </Card>
  );
}

export function BillingPage() {
  const { t } = useTranslation('portal');
  const invoices = useMyInvoicesQuery();

  if (invoices.isLoading) {
    return <BillingPageSkeleton label="Loading billing" />;
  }

  if (invoices.isError) {
    return <PortalLoadError onRetry={() => void invoices.refetch()} />;
  }

  const data = invoices.data;
  const currency = data?.currency ?? 'INR';
  const balance = data?.outstandingBalance ?? 0;
  const list = data?.invoices ?? [];
  const settled = balance <= 0;

  return (
    <div className="flex flex-col gap-6">
      <h1 className="text-[23px] font-bold tracking-[-0.01em] md:hidden">{t('billing.title', 'Billing')}</h1>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="h-fit p-5 text-center lg:col-span-1">
          <div className="text-[12.5px] text-[var(--text-2)]">{t('billing.currentBalance', 'Current balance')}</div>
          <div className={`my-1 text-[30px] font-bold ${settled ? 'text-success' : 'text-[var(--text)]'}`}>
            {numberUtils.formatCurrency(balance, currency)}
          </div>
          <div className="text-[12.5px] text-[var(--text-3)]">
            {settled ? t('billing.settledUp', "You're all settled up") : t('billing.payAtClinic', 'Pay at clinic')}
          </div>
        </Card>

        <section className="flex flex-col gap-3 lg:col-span-2">
          <h2 className="text-[12px] font-bold uppercase tracking-[0.05em] text-[var(--text-3)]">
            {t('billing.historySection', 'History')}
          </h2>
          {list.length > 0 ? (
            list.map((invoice) => <InvoiceRow key={invoice.id} invoice={invoice} />)
          ) : (
            <p className="rounded-[14px] border border-dashed border-[var(--border-strong)] p-4 text-center text-[13px] text-[var(--text-3)]">
              {t('billing.noInvoices', 'No billing history yet.')}
            </p>
          )}
        </section>
      </div>
    </div>
  );
}
